import { computed } from "vue";
import { useNuxtApp, useState } from "#imports";

const PDZ_ORGANIZATION_INN = "7706416719";

const normalizeText = (value: unknown) => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

const parseNumber = (value: unknown) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const cleaned = value.replace(/[\s\u00A0]/g, "").replace(",", ".");
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const getTodayParts = () => {
  const today = new Date();
  return {
    day: String(today.getDate()),
    month: String(today.getMonth() + 1),
    year: String(today.getFullYear()),
  };
};

const formatCurrency = (value: number) => {
  const hasFraction = Math.round(value * 100) % 100 !== 0;
  return `${value.toLocaleString("ru-RU", {
    minimumFractionDigits: hasFraction ? 2 : 0,
    maximumFractionDigits: 2,
  })} ₽`;
};

export const useClientPdz = () => {
  const nuxtApp = useNuxtApp();
  const $useOneCApi = nuxtApp.$useOneCApi as (
    action: string,
    payload?: Record<string, any>,
  ) => Promise<any>;

  const amount = useState<number>("client-pdz:amount", () => 0);
  const loading = useState<boolean>("client-pdz:loading", () => false);
  const loaded = useState<boolean>("client-pdz:loaded", () => false);
  const inn = useState<string>("client-pdz:inn", () => PDZ_ORGANIZATION_INN);

  const fetchPdz = async (force = false) => {
    if (!process.client || typeof $useOneCApi !== "function") return;

    const currentInn = PDZ_ORGANIZATION_INN;
    inn.value = currentInn;

    if (loading.value) return;
    if (!force && loaded.value && inn.value === currentInn) return;

    loading.value = true;

    try {
      const today = getTodayParts();
      const response = await $useOneCApi("GetBalanceClient", {
        INNOrganization: currentInn,
        to_day: today.day,
        to_month: today.month,
        to_year: today.year,
      });

      const status = normalizeText(response?.["Статус"]);
      if (status && status !== "OK") {
        throw new Error(
          normalizeText(response?.["ОписаниеОшибки"]) ||
            "Ошибка получения ПДЗ",
        );
      }

      const nextAmount = parseNumber(response?.["ДолгКлиента"]);

      amount.value = nextAmount > 0 ? nextAmount : 0;
    } catch (error) {
      console.error("Не удалось загрузить ПДЗ", error);
      amount.value = 0;
    } finally {
      loaded.value = true;
      loading.value = false;
    }
  };

  const hasPdz = computed(() => amount.value > 0);
  const formattedPdz = computed(() =>
    hasPdz.value ? formatCurrency(amount.value) : "",
  );

  return {
    amount,
    loading,
    loaded,
    inn,
    hasPdz,
    formattedPdz,
    fetchPdz,
  };
};
